import { useEffect, useRef, useState } from "react";
import { Search, Loader2, Package, AlertTriangle } from "lucide-react";
import { api, money } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

/**
 * Inventory part picker. Opens a searchable list of stock parts and returns the
 * chosen part via onPick. Gracefully degrades if Parts is a Pro-gated feature.
 */
export function PartPicker({ open, onClose, onPick }) {
  const [q, setQ] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [gated, setGated] = useState(false);
  const timer = useRef(null);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      api(`/parts?q=${encodeURIComponent(q)}`)
        .then((r) => { setRows(r || []); setGated(false); })
        .catch((e) => { if (e.status === 402) setGated(true); setRows([]); })
        .finally(() => setLoading(false));
    }, 250);
    return () => clearTimeout(timer.current);
  }, [q, open]);

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader><DialogTitle className="font-display uppercase">Add part from stock</DialogTitle></DialogHeader>
        <DialogDescription className="sr-only">Search and pick an inventory part to add.</DialogDescription>
        <div className="relative mb-3">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name or SKU…" data-testid="partpicker-search"
            className="w-full rounded-lg border border-input bg-card py-2.5 pl-10 pr-3 text-sm outline-none focus:border-brand focus:ring-2 focus:ring-brand/20" />
        </div>
        {gated ? (
          <div className="flex flex-col items-center gap-2 py-8 text-center" data-testid="partpicker-gated">
            <Package className="h-8 w-8 text-brand" />
            <p className="text-sm font-semibold text-midnight">Inventory is a Pro feature</p>
            <p className="text-xs text-muted-foreground">You can still add parts manually.</p>
          </div>
        ) : loading ? (
          <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-brand" /></div>
        ) : rows.length === 0 ? (
          <p className="py-8 text-center text-sm text-muted-foreground" data-testid="partpicker-empty">{q ? "No matching parts in stock." : "No parts in stock yet."}</p>
        ) : (
          <div className="max-h-80 divide-y divide-border overflow-y-auto rounded-lg border border-border">
            {rows.map((p) => {
              const low = (p.stock_qty ?? 0) <= 5;
              return (
                <button key={p.id} onClick={() => { onPick(p); onClose(); }} data-testid={`partpicker-row-${p.id}`}
                  className="flex w-full items-center gap-3 px-3 py-2.5 text-left hover:bg-secondary">
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-bold text-midnight">{p.name}</p>
                    <p className="flex items-center gap-2 text-xs text-muted-foreground">
                      {p.sku && <span>SKU {p.sku}</span>}
                      <span className={`inline-flex items-center gap-1 ${low ? "font-semibold text-warning" : ""}`}>
                        {low && <AlertTriangle className="h-3 w-3" />}{p.stock_qty ?? 0} in stock
                      </span>
                    </p>
                  </div>
                  <span className="font-display text-sm font-bold text-midnight">{money(p.unit_price)}</span>
                </button>
              );
            })}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
